import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from '../components/Layout';
import AppNavigationWrapper from '../components/AppNavigationWrapper';
import { useAuthNavigation } from '../hooks/useAuthNavigation';

// All page components below are lazy-loaded: each route's code splits into
// its own chunk (grouped by feature via webpackChunkName) instead of riding
// in the initial bundle. createLazyRoute wraps each in Suspense with a
// page-level loading spinner.
import { createLazyRoute } from '../utils/lazy-loading';

// Critical / hot-path routes (auth, home, feed index, training log) live in
// their own pre-defined chunks.
import * as CriticalLazyRoutes from './critical-lazy-routes';

// Structural shells kept eager: they wrap the very first paint, so a lazy
// boundary here would only add a spinner flash.
import WelcomePage from '../pages/auth/WelcomePage';
import SimpleAuthLayout from '../components/templates/SimpleAuthLayout';
import NotFoundPage from '../pages/NotFoundPage';

// Feed
const PostDetailPage = createLazyRoute(() => import(/* webpackChunkName: "feed" */ '../pages/feed/PostDetailPage'));

// Training
const TrainingListPage = createLazyRoute(() => import(/* webpackChunkName: "training" */ '../pages/training/TrainingListPage'));
const SafeTrainingDetailPage = createLazyRoute(() => import(/* webpackChunkName: "training" */ '../pages/training/SafeTrainingDetailPage'));
const TrainingDataPage = createLazyRoute(() => import(/* webpackChunkName: "training" */ '../pages/training/TrainingDataPage'));
const AddTrainingPage = createLazyRoute(() => import(/* webpackChunkName: "training" */ '../pages/training/AddTrainingPage'));
const ShareTrainingPage = createLazyRoute(() => import(/* webpackChunkName: "training" */ '../pages/training/ShareTrainingPage'));
const WhereYouveTrainedPage = createLazyRoute(() => import(/* webpackChunkName: "training" */ '../pages/training/WhereYouveTrainedPage'));
const RoundTimerPage = createLazyRoute(() => import(/* webpackChunkName: "training" */ '../pages/training/RoundTimerPage'));

// Routines & workout sessions
const RoutinesPageSimple = createLazyRoute(() => import(/* webpackChunkName: "routines" */ '../pages/training/RoutinesPageSimple'));
const ViewRoutinePage = createLazyRoute(() => import(/* webpackChunkName: "routines" */ '../pages/training/ViewRoutinePage'));
const EditRoutinePage = createLazyRoute(() => import(/* webpackChunkName: "routines" */ '../pages/workout-routines/EditRoutinePage'));
const WorkoutSessionPage = createLazyRoute(() => import(/* webpackChunkName: "routines" */ '../pages/workout-sessions/WorkoutSessionPage'));

// Stats
const TabTrainingLog = createLazyRoute(() => import(/* webpackChunkName: "stats" */ '../pages/stats/TabTrainingLog'));

// Profile
const ProfilePage = createLazyRoute(() => import(/* webpackChunkName: "profile" */ '../pages/profile/ProfilePage'));
const ViewProfilePage = createLazyRoute(() => import(/* webpackChunkName: "profile" */ '../pages/profile/ViewProfilePage'));
const EditProfilePage = createLazyRoute(() => import(/* webpackChunkName: "profile" */ '../pages/profile/EditProfilePage'));

// Social
const FriendRequestsPage = createLazyRoute(() => import(/* webpackChunkName: "social" */ '../pages/social/FriendRequestsPage'));
const CommentsPage = createLazyRoute(() => import(/* webpackChunkName: "social" */ '../pages/comments/CommentsPage'));
const AddFriendsPage = createLazyRoute(() => import(/* webpackChunkName: "social" */ '../pages/friends/AddFriendsPage'));
const FriendSearchPage = createLazyRoute(() => import(/* webpackChunkName: "social" */ '../pages/friends/FriendSearchPage'));
const FriendsPage = createLazyRoute(() => import(/* webpackChunkName: "social" */ '../pages/friends/FriendsPage'));

// Injuries & custom metrics
const AddInjuryPage = createLazyRoute(() => import(/* webpackChunkName: "features" */ '../pages/injuries/AddInjuryPage'));
const InjuriesListPage = createLazyRoute(() => import(/* webpackChunkName: "features" */ '../pages/injuries/InjuriesListPage'));
const CustomMetricsPage = createLazyRoute(() => import(/* webpackChunkName: "features" */ '../pages/custom-metrics/CustomMetricsPage'));
const AddEditMetricPage = createLazyRoute(() => import(/* webpackChunkName: "features" */ '../pages/custom-metrics/AddEditMetricPage'));

// Exercises
const ExerciseListPage = createLazyRoute(() => import(/* webpackChunkName: "fitness" */ '../pages/exercise/ExerciseListPage'));
const ExerciseDetailPage = createLazyRoute(() => import(/* webpackChunkName: "fitness" */ '../pages/exercise/ExerciseDetailPage'));
const ExerciseCreatePage = createLazyRoute(() => import(/* webpackChunkName: "fitness" */ '../pages/exercise/ExerciseCreatePage'));
const ExerciseEditPage = createLazyRoute(() => import(/* webpackChunkName: "fitness" */ '../pages/exercise/ExerciseEditPage'));

// Add menu
const AddPage = createLazyRoute(() => import(/* webpackChunkName: "add" */ '../pages/add/AddPage'));
const AddTechniquePage = createLazyRoute(() => import(/* webpackChunkName: "add" */ '../pages/add/AddTechniquePage'));

// Belt
const BeltProgressionPage = createLazyRoute(() => import(/* webpackChunkName: "belt" */ '../pages/belt/BeltProgressionPage'));
const AddPromotionPage = createLazyRoute(() => import(/* webpackChunkName: "belt" */ '../pages/belt/AddPromotionPage'));
const ShareBeltPromotionPage = createLazyRoute(() => import(/* webpackChunkName: "belt" */ '../pages/belt/ShareBeltPromotionPage'));

// Competition
const CompetitionsPage = createLazyRoute(() => import(/* webpackChunkName: "competition" */ '../pages/competition/CompetitionsPage'));
const AddCompetitionPageSimple = createLazyRoute(() => import(/* webpackChunkName: "competition" */ '../pages/competition/AddCompetitionPageSimple'));
const ShareCompetitionPage = createLazyRoute(() => import(/* webpackChunkName: "competition" */ '../pages/competition/ShareCompetitionPage'));
const CompetitionDetailPage = createLazyRoute(() => import(/* webpackChunkName: "competition" */ '../pages/competition/CompetitionDetailPage'));
const AddMatchPage = createLazyRoute(() => import(/* webpackChunkName: "competition" */ '../pages/competition/AddMatchPage'));
const EditMatchPage = createLazyRoute(() => import(/* webpackChunkName: "competition" */ '../pages/competition/EditMatchPage'));
const MatchDetailPage = createLazyRoute(() => import(/* webpackChunkName: "competition" */ '../pages/competition/MatchDetailPage'));

// Goals & achievements
const GoalsListPage = createLazyRoute(() => import(/* webpackChunkName: "goals" */ '../pages/goals/GoalsListPage'));
const GoalDetailPage = createLazyRoute(() => import(/* webpackChunkName: "goals" */ '../pages/goals/GoalDetailPage'));
const CreateGoalPage = createLazyRoute(() => import(/* webpackChunkName: "goals" */ '../pages/goals/CreateGoalPage'));
const EditGoalPage = createLazyRoute(() => import(/* webpackChunkName: "goals" */ '../pages/goals/EditGoalPage'));
const AchievementsPage = createLazyRoute(() => import(/* webpackChunkName: "goals" */ '../pages/goals/AchievementsPage'));
const AchievementDetailPage = createLazyRoute(() => import(/* webpackChunkName: "goals" */ '../pages/goals/AchievementDetailPage'));
const TemplateSelectionPage = createLazyRoute(() => import(/* webpackChunkName: "goals" */ '../pages/goals/TemplateSelectionPage'));
const TemplatesManagementPage = createLazyRoute(() => import(/* webpackChunkName: "goals" */ '../pages/goals/TemplatesManagementPage'));

// Weight tracker
const WeightTrackerPage = createLazyRoute(() => import(/* webpackChunkName: "weight" */ '../pages/weight/WeightTrackerPage'));
const AddWeightEntryPage = createLazyRoute(() => import(/* webpackChunkName: "weight" */ '../pages/weight/AddWeightEntryPage'));
const EditWeightEntryPage = createLazyRoute(() => import(/* webpackChunkName: "weight" */ '../pages/weight/EditWeightEntryPage'));
const EditWeightGoalPage = createLazyRoute(() => import(/* webpackChunkName: "weight" */ '../pages/weight/EditWeightGoalPage'));
const WeightHistoryPage = createLazyRoute(() => import(/* webpackChunkName: "weight" */ '../pages/weight/WeightHistoryPage'));

// Premium
const PremiumLandingPage = createLazyRoute(() => import(/* webpackChunkName: "premium" */ '../pages/premium/PremiumLandingPage'));
const PremiumInsightsPage = createLazyRoute(() => import(/* webpackChunkName: "premium" */ '../pages/premium/PremiumInsightsPage'));
const RestorePurchasesPage = createLazyRoute(() => import(/* webpackChunkName: "premium" */ '../pages/premium/RestorePurchasesPage'));
const PromoCodesPage = createLazyRoute(() => import(/* webpackChunkName: "premium" */ '../pages/premium/PromoCodesPage'));
const WelcomeToPeakPage = createLazyRoute(() => import(/* webpackChunkName: "premium" */ '../pages/premium/WelcomeToPeakPage'));
const PeakInsightsPage = createLazyRoute(() => import(/* webpackChunkName: "premium" */ '../pages/insights/PeakInsightsPage'));

// AI / Coach
const ChatPage = createLazyRoute(() => import(/* webpackChunkName: "ai" */ '../pages/ai/ChatPage'));
const AnalysisPage = createLazyRoute(() => import(/* webpackChunkName: "ai" */ '../pages/ai/AnalysisPage'));
const AIDiaryPage = createLazyRoute(() => import(/* webpackChunkName: "ai" */ '../pages/ai/AIDiaryPage'));
const RecommendationsPage = createLazyRoute(() => import(/* webpackChunkName: "ai" */ '../pages/ai/RecommendationsPage'));
const CoachMenuPage = createLazyRoute(() => import(/* webpackChunkName: "ai" */ '../pages/coach/CoachMenuPage'));

// Settings
const SettingsPage = createLazyRoute(() => import(/* webpackChunkName: "settings" */ '../pages/settings/SettingsPage'));
const PendingChangesPage = createLazyRoute(() => import(/* webpackChunkName: "settings" */ '../pages/settings/PendingChangesPage'));
const CustomBeltConfigurator = createLazyRoute(() => import(/* webpackChunkName: "settings" */ '../pages/settings/CustomBeltConfigurator'));
const ConnectedServicesPage = createLazyRoute(() => import(/* webpackChunkName: "settings" */ '../pages/settings/ConnectedServicesPage'));
const ImportWorkoutsPage = createLazyRoute(() => import(/* webpackChunkName: "settings" */ '../pages/settings/ImportWorkoutsPage'));
const MorePage = createLazyRoute(() => import(/* webpackChunkName: "settings" */ '../pages/more/MorePage'));
const WhatsNewPage = createLazyRoute(() => import(/* webpackChunkName: "settings" */ '../pages/whats-new/WhatsNewPage'));

// Notifications
const NotificationsPage = createLazyRoute(() => import(/* webpackChunkName: "notifications" */ '../pages/notifications/NotificationsPage'));

// Leaderboard
const LeaderboardPage = createLazyRoute(() => import(/* webpackChunkName: "leaderboard" */ '../pages/leaderboard/LeaderboardPage'));

// Gym timer
const GymTimerJoinPage = createLazyRoute(() => import(/* webpackChunkName: "gymtimer" */ '../pages/gymtimer/GymTimerJoinPage'));
const GymTimerInfoPage = createLazyRoute(() => import(/* webpackChunkName: "gymtimer" */ '../pages/gymtimer/GymTimerInfoPage'));
const CodeEntryPage = createLazyRoute(() => import(/* webpackChunkName: "gymtimer" */ '../pages/gymtimer/CodeEntryPage'));
const GymTimerPage = createLazyRoute(() => import(/* webpackChunkName: "gymtimer" */ '../pages/troubleshoot/GymTimerPage'));

// Help / Legal
const AboutPage = createLazyRoute(() => import(/* webpackChunkName: "help" */ '../pages/help-legal/AboutPage'));
const TermsPage = createLazyRoute(() => import(/* webpackChunkName: "help" */ '../pages/help-legal/TermsPage'));
const PrivacyPage = createLazyRoute(() => import(/* webpackChunkName: "help" */ '../pages/help-legal/PrivacyPage'));
const HelpPage = createLazyRoute(() => import(/* webpackChunkName: "help" */ '../pages/help-legal/HelpPage'));
const FeedbackPage = createLazyRoute(() => import(/* webpackChunkName: "help" */ '../pages/help-legal/FeedbackPage'));

// Training Wiki
const TrainingWikiPage = createLazyRoute(() => import(/* webpackChunkName: "wiki" */ '../pages/wiki/TrainingWikiPage'));
const TechniqueDetailPageEnhanced = createLazyRoute(() => import(/* webpackChunkName: "wiki" */ '../pages/wiki/TechniqueDetailPageEnhanced'));
const SearchTechniquesPage = createLazyRoute(() => import(/* webpackChunkName: "wiki" */ '../pages/wiki/SearchTechniquesPage'));

// Gi keeper
const GiKeeperPage = createLazyRoute(() => import(/* webpackChunkName: "gi" */ '../pages/gi/GiKeeperPage'));
const AddEditGiPage = createLazyRoute(() => import(/* webpackChunkName: "gi" */ '../pages/gi/AddEditGiPage'));

// Schools directory
const SchoolsDirectoryPage = createLazyRoute(() => import(/* webpackChunkName: "schools" */ '../pages/schools/SchoolsDirectoryPage'));
const SchoolDetailPage = createLazyRoute(() => import(/* webpackChunkName: "schools" */ '../pages/schools/SchoolDetailPage'));
const AddSchoolPage = createLazyRoute(() => import(/* webpackChunkName: "schools" */ '../pages/schools/AddSchoolPage'));
const ClaimSchoolPage = createLazyRoute(() => import(/* webpackChunkName: "schools" */ '../pages/schools/ClaimSchoolPage'));
const EditSchoolPage = createLazyRoute(() => import(/* webpackChunkName: "schools" */ '../pages/schools/EditSchoolPage'));

// ClearMat Standard (large feature surface, rarely used by core users)
const ClearMatDirectoryPage = createLazyRoute(() => import(/* webpackChunkName: "clearmat" */ '../pages/clearmat/ClearMatDirectoryPage'));
const ClearMatSchoolDetailPage = createLazyRoute(() => import(/* webpackChunkName: "clearmat" */ '../pages/clearmat/ClearMatSchoolDetailPage'));
const ClearMatAddSchoolPage = createLazyRoute(() => import(/* webpackChunkName: "clearmat" */ '../pages/clearmat/ClearMatAddSchoolPage'));
const IncidentReportPage = createLazyRoute(() => import(/* webpackChunkName: "clearmat" */ '../pages/clearmat/IncidentReportPage'));
const IncidentDetailPage = createLazyRoute(() => import(/* webpackChunkName: "clearmat" */ '../pages/clearmat/IncidentDetailPage'));
const EventsPage = createLazyRoute(() => import(/* webpackChunkName: "clearmat" */ '../pages/clearmat/EventsPage'));
const EventDetailPage = createLazyRoute(() => import(/* webpackChunkName: "clearmat" */ '../pages/clearmat/EventDetailPage'));
const SubmitEventPage = createLazyRoute(() => import(/* webpackChunkName: "clearmat" */ '../pages/clearmat/SubmitEventPage'));
const BlogPage = createLazyRoute(() => import(/* webpackChunkName: "clearmat" */ '../pages/clearmat/BlogPage'));
const BlogArticlePage = createLazyRoute(() => import(/* webpackChunkName: "clearmat" */ '../pages/clearmat/BlogArticlePage'));
const BlogEditorPage = createLazyRoute(() => import(/* webpackChunkName: "clearmat" */ '../pages/clearmat/BlogEditorPage'));
const MyArticlesPage = createLazyRoute(() => import(/* webpackChunkName: "clearmat" */ '../pages/clearmat/MyArticlesPage'));

// ClearMat admin
const ClearMatAdminDashboard = createLazyRoute(() => import(/* webpackChunkName: "clearmat-admin" */ '../pages/clearmat/admin/ClearMatAdminDashboard'));
const IncidentModerationPage = createLazyRoute(() => import(/* webpackChunkName: "clearmat-admin" */ '../pages/clearmat/admin/IncidentModerationPage'));
const AuditLogPage = createLazyRoute(() => import(/* webpackChunkName: "clearmat-admin" */ '../pages/clearmat/admin/AuditLogPage'));
const ClearMatSchoolManagementPage = createLazyRoute(() => import(/* webpackChunkName: "clearmat-admin" */ '../pages/clearmat/admin/ClearMatSchoolManagementPage'));
const ModeratorManagementPage = createLazyRoute(() => import(/* webpackChunkName: "clearmat-admin" */ '../pages/clearmat/admin/ModeratorManagementPage'));
const ResourceManagementPage = createLazyRoute(() => import(/* webpackChunkName: "clearmat-admin" */ '../pages/clearmat/admin/ResourceManagementPage'));
const EventModerationPage = createLazyRoute(() => import(/* webpackChunkName: "clearmat-admin" */ '../pages/clearmat/admin/EventModerationPage'));
const ReviewModerationPage = createLazyRoute(() => import(/* webpackChunkName: "clearmat-admin" */ '../pages/clearmat/admin/ReviewModerationPage'));
const ResponseModerationPage = createLazyRoute(() => import(/* webpackChunkName: "clearmat-admin" */ '../pages/clearmat/admin/ResponseModerationPage'));
const ContributorManagementPage = createLazyRoute(() => import(/* webpackChunkName: "clearmat-admin" */ '../pages/clearmat/admin/ContributorManagementPage'));
const EditorManagementPage = createLazyRoute(() => import(/* webpackChunkName: "clearmat-admin" */ '../pages/clearmat/admin/EditorManagementPage'));
const BlogModerationPage = createLazyRoute(() => import(/* webpackChunkName: "clearmat-admin" */ '../pages/clearmat/admin/BlogModerationPage'));

// Admin
const AdminDashboard = createLazyRoute(() => import(/* webpackChunkName: "admin" */ '../pages/admin/AdminDashboard'));
const UserManagementPage = createLazyRoute(() => import(/* webpackChunkName: "admin" */ '../pages/admin/UserManagementPage'));
const DatabaseManagementPage = createLazyRoute(() => import(/* webpackChunkName: "admin" */ '../pages/admin/DatabaseManagementPage'));
const ActivityMonitoringPage = createLazyRoute(() => import(/* webpackChunkName: "admin" */ '../pages/admin/ActivityMonitoringPage'));
const SchoolManagementPage = createLazyRoute(() => import(/* webpackChunkName: "admin" */ '../pages/admin/SchoolManagementPage'));
const PeakSubscriptionPage = createLazyRoute(() => import(/* webpackChunkName: "admin" */ '../pages/admin/PeakSubscriptionPage'));
const BJJLocationsImport = createLazyRoute(() => import(/* webpackChunkName: "admin" */ '../pages/admin/BJJLocationsImport'));

const AppRoutes: React.FC = () => {
  // Handle auth-based navigation
  useAuthNavigation();

  return (
    <AppNavigationWrapper>
      <Routes>
      {/* Welcome page (public, shown before auth) */}
      <Route
        path="/welcome"
        element={<WelcomePage />}
      />

      {/* Public routes (login, register, etc.) */}
      <Route path="/auth" element={<SimpleAuthLayout />}>
        <Route
          path="login"
          element={<CriticalLazyRoutes.LoginPage />}
        />
        <Route
          path="register"
          element={<CriticalLazyRoutes.RegisterPage />}
        />
        <Route
          path="reset-password"
          element={<CriticalLazyRoutes.ResetPasswordPage />}
        />
        {/* Debug route removed for production
        <Route
          path="debug"
          element={<LoginDebugPage />}
        />
        */}
      </Route>

      {/* Public legal routes (no auth required - for App Store compliance) */}
      <Route
        path="/terms"
        element={
          <Layout headerTitle="Terms of Service" showBackButton>
            <TermsPage />
          </Layout>
        }
      />
      <Route
        path="/privacy"
        element={
          <Layout headerTitle="Privacy Policy" showBackButton>
            <PrivacyPage />
          </Layout>
        }
      />

      {/* Protected routes */}
      <Route
        path="/feed"
        element={<Navigate to="/home" replace />}
      />
      <Route
        path="/post/:id"
        element={

            <Layout headerTitle="Post" showBackButton>
              <PostDetailPage />
            </Layout>

        }
      />

      {/* Training routes */}
      <Route
        path="/training"
        element={
          
            <Layout headerTitle="Activity Log">
              <CriticalLazyRoutes.UnifiedTrainingLogPage />
            </Layout>
          
        }
      />
      <Route
        path="/training-list"
        element={
          
            <Layout headerTitle="Training Sessions">
              <TrainingListPage />
            </Layout>
          
        }
      />
      <Route
        path="/training-data"
        element={
          
            <Layout headerTitle="Training Data">
              <TrainingDataPage />
            </Layout>
          
        }
      />
      <Route
        path="/training/add"
        element={
          
            <Layout showBackButton headerTitle="Add Training">
              <AddTrainingPage />
            </Layout>
          
        }
      />
      <Route
        path="/training/:id"
        element={
          
            <Layout showBackButton headerTitle="Training Details">
              <SafeTrainingDetailPage />
            </Layout>
          
        }
      />
      <Route
        path="/training/:id/edit"
        element={
          
            <Layout showBackButton headerTitle="Edit Training">
              <AddTrainingPage />
            </Layout>
          
        }
      />
      <Route
        path="/training/:trainingId/share"
        element={
          
            <Layout showBackButton headerTitle="Share Training">
              <ShareTrainingPage />
            </Layout>
          
        }
      />
      
      {/* Training Wiki Routes */}
      <Route
        path="/wiki"
        element={
          
            <Layout headerTitle="Training Wiki">
              <TrainingWikiPage />
            </Layout>
          
        }
      />
      <Route
        path="/wiki/search"
        element={
          
            <Layout showBackButton headerTitle="Search Techniques">
              <SearchTechniquesPage />
            </Layout>
          
        }
      />
      <Route
        path="/wiki/technique/:techniqueId"
        element={
          
            <Layout showBackButton headerTitle="Technique Details">
              <TechniqueDetailPageEnhanced />
            </Layout>
          
        }
      />
      <Route
        path="/wiki/technique/new"
        element={
          
            <Layout showBackButton headerTitle="Add Technique">
              <AddTechniquePage />
            </Layout>
          
        }
      />
      <Route
        path="/wiki/technique/edit/:techniqueId"
        element={
          
            <Layout showBackButton headerTitle="Edit Technique">
              <AddTechniquePage />
            </Layout>
          
        }
      />
      {/* Debug route removed for production
      <Route
        path="/wiki/debug"
        element={

            <Layout showBackButton headerTitle="Debug Personal Wiki">
              <DebugPersonalWiki />
            </Layout>

        }
      />
      */}
      
      {/* Add Page route */}
      <Route
        path="/add"
        element={
          
            <Layout headerTitle="Add New">
              <AddPage />
            </Layout>
          
        }
      />
      <Route
        path="/add-training"
        element={
          <Layout headerTitle="Add Training" showBackButton>
            <AddTrainingPage />
          </Layout>
        }
      />
      <Route
        path="/competitions"
        element={
          
            <Layout headerTitle="Competitions">
              <CompetitionsPage />
            </Layout>
          
        }
      />
      <Route
        path="/add-competition"
        element={
          
            <Layout headerTitle="Add Competition" showBackButton>
              <AddCompetitionPageSimple />
            </Layout>
          
        }
      />
      <Route
        path="/add-promotion"
        element={
          
            <Layout headerTitle="Add Belt Promotion" showBackButton>
              <AddPromotionPage />
            </Layout>
          
        }
      />
      
      {/* Belt progression routes */}
      <Route
        path="/belt-progression"
        element={
          
            <Layout headerTitle="Belt Progression">
              <BeltProgressionPage />
            </Layout>
          
        }
      />
      <Route
        path="/edit-promotion/:id"
        element={
          
            <Layout headerTitle="Edit Promotion" showBackButton>
              <AddPromotionPage />
            </Layout>
          
        }
      />
      <Route
        path="/belt-promotion/:promotionId/share"
        element={

            <Layout headerTitle="Share Promotion" showBackButton>
              <ShareBeltPromotionPage />
            </Layout>

        }
      />
      <Route
        path="/competition/:competitionId/share"
        element={
          
            <Layout headerTitle="Share Competition" showBackButton>
              <ShareCompetitionPage />
            </Layout>
          
        }
      />
      <Route
        path="/competition/:competitionId"
        element={
          
            <Layout headerTitle="Competition Details" showBackButton>
              <CompetitionDetailPage />
            </Layout>
          
        }
      />
      <Route
        path="/competition/:competitionId/edit"
        element={
          
            <Layout headerTitle="Edit Competition" showBackButton>
              <AddCompetitionPageSimple />
            </Layout>
          
        }
      />
      <Route
        path="/competition/:competitionId/match/add"
        element={
          
            <Layout headerTitle="Add Match" showBackButton>
              <AddMatchPage />
            </Layout>
          
        }
      />
      <Route
        path="/competition/:competitionId/match/:matchId"
        element={
          
            <Layout headerTitle="Match Details" showBackButton>
              <MatchDetailPage />
            </Layout>
          
        }
      />
      <Route
        path="/competition/:competitionId/match/:matchId/edit"
        element={
          
            <Layout headerTitle="Edit Match" showBackButton>
              <EditMatchPage />
            </Layout>
          
        }
      />
      <Route
        path="/add-technique"
        element={
          
            <Layout headerTitle="Add Technique" showBackButton>
              <AddTechniquePage />
            </Layout>
          
        }
      />
      
      {/* Stats route */}
      <Route
        path="/stats"
        element={
          
            <Layout headerTitle="Training Stats">
              <TabTrainingLog />
            </Layout>
          
        }
      />
      
      {/* Profile routes */}
      <Route
        path="/profile"
        element={
          
            <Layout headerTitle="My Profile">
              <ProfilePage />
            </Layout>
          
        }
      />
      <Route
        path="/profile/edit"
        element={<EditProfilePage />}
      />
      <Route
        path="/profile/:username"
        element={
          
            <Layout showBackButton headerTitle="Profile">
              <ProfilePage />
            </Layout>
          
        }
      />
      <Route
        path="/user/:userId"
        element={
          
            <Layout showBackButton headerTitle="User Profile">
              <ViewProfilePage />
            </Layout>
          
        }
      />

      {/* Friends routes */}
      <Route
        path="/friends"
        element={

            <Layout headerTitle="Friends">
              <FriendsPage />
            </Layout>

        }
      />
      <Route
        path="/user/:userId/friends"
        element={

            <Layout headerTitle="Friends" showBackButton>
              <FriendsPage />
            </Layout>

        }
      />
      <Route
        path="/friends/add"
        element={
          
            <Layout headerTitle="Add Friends" showBackButton>
              <AddFriendsPage />
            </Layout>
          
        }
      />
      <Route
        path="/friends/search"
        element={
          
            <Layout headerTitle="Find Friends" showBackButton>
              <FriendSearchPage />
            </Layout>
          
        }
      />
      <Route
        path="/friends/requests"
        element={<FriendRequestsPage />}
      />

      {/* Injury tracking routes */}
      <Route
        path="/injuries"
        element={
          
            <Layout headerTitle="Injury Tracking">
              <InjuriesListPage />
            </Layout>
          
        }
      />
      <Route
        path="/injuries/add"
        element={
          
            <Layout headerTitle="Add Injury" showBackButton>
              <AddInjuryPage />
            </Layout>
          
        }
      />
      <Route
        path="/injuries/edit/:injuryId"
        element={
          
            <Layout headerTitle="Edit Injury" showBackButton>
              <AddInjuryPage />
            </Layout>
          
        }
      />

      {/* Training locations route */}
      <Route
        path="/where-trained"
        element={
          
            <Layout headerTitle="Where You've Trained" showBackButton>
              <WhereYouveTrainedPage />
            </Layout>
          
        }
      />

      {/* Custom metrics routes */}
      <Route
        path="/custom-metrics"
        element={
          
            <Layout headerTitle="Custom Metrics" showBackButton>
              <CustomMetricsPage />
            </Layout>
          
        }
      />
      <Route
        path="/custom-metrics/add"
        element={
          
            <Layout headerTitle="Add Custom Metric" showBackButton>
              <AddEditMetricPage />
            </Layout>
          
        }
      />
      <Route
        path="/custom-metrics/edit/:metricId"
        element={
          
            <Layout headerTitle="Edit Custom Metric" showBackButton>
              <AddEditMetricPage />
            </Layout>
          
        }
      />

      {/* Exercise routes */}
      <Route
        path="/exercises"
        element={
          
            <Layout headerTitle="My Exercises">
              <ExerciseListPage />
            </Layout>
          
        }
      />
      <Route
        path="/exercise/create"
        element={
          
            <Layout headerTitle="Add Exercise" showBackButton>
              <ExerciseCreatePage />
            </Layout>
          
        }
      />
      <Route
        path="/exercise/:authorId/:exerciseId"
        element={
          
            <Layout headerTitle="Exercise Details" showBackButton>
              <ExerciseDetailPage />
            </Layout>
          
        }
      />
      <Route
        path="/exercise/edit/:authorId/:exerciseId"
        element={
          
            <Layout headerTitle="Edit Exercise" showBackButton>
              <ExerciseEditPage />
            </Layout>
          
        }
      />

      {/* Workout Routine routes - ORDER MATTERS! Specific paths before parameterized */}
      <Route
        path="/workout-routines"
        element={

            <Layout headerTitle="Workout Routines">
              <RoutinesPageSimple />
            </Layout>

        }
      />
      <Route
        path="/workout-routines/new"
        element={

            <Layout headerTitle="Create Routine" showBackButton>
              <EditRoutinePage />
            </Layout>

        }
      />
      <Route
        path="/workout-routines/edit/:routineId"
        element={

            <Layout headerTitle="Edit Routine" showBackButton>
              <EditRoutinePage />
            </Layout>

        }
      />
      <Route
        path="/workout-routines/:routineId/session"
        element={

            <WorkoutSessionPage />

        }
      />
      <Route
        path="/workout-routines/:routineId"
        element={

            <Layout headerTitle="Workout Routine" showBackButton>
              <ViewRoutinePage />
            </Layout>

        }
      />

      {/* Round Timer route */}
      <Route
        path="/timer"
        element={

            <RoundTimerPage />

        }
      />

      {/* Gym Timer Info page - shows Apple TV app details and code entry */}
      <Route
        path="/gymtimer/info"
        element={
            <GymTimerInfoPage />
        }
      />

      {/* Gym Timer Join route - for athletes scanning QR codes */}
      <Route
        path="/gymtimer/join/:pairingCode"
        element={

            <GymTimerJoinPage />

        }
      />

      {/* Code Entry page - unified for TV pairing and session joining */}
      <Route
        path="/code-entry"
        element={

            <CodeEntryPage />

        }
      />

      {/* Marune Timer - Web scorekeeper for gym timer sessions */}
      <Route
        path="/marunetimer"
        element={
            <GymTimerPage />
        }
      />

      {/* Goals routes */}
      <Route
        path="/goals"
        element={
          
            <Layout headerTitle="Goals">
              <GoalsListPage />
            </Layout>
          
        }
      />
      <Route
        path="/goals/new"
        element={
          
            <Layout headerTitle="Create Goal" showBackButton>
              <CreateGoalPage />
            </Layout>
          
        }
      />
      <Route
        path="/goals/:goalId"
        element={
          
            <Layout headerTitle="Goal Details" showBackButton>
              <GoalDetailPage />
            </Layout>
          
        }
      />
      <Route
        path="/goals/:goalId/edit"
        element={
          
            <Layout headerTitle="Edit Goal" showBackButton>
              <EditGoalPage />
            </Layout>
          
        }
      />
      <Route
        path="/goals/templates"
        element={
          
            <Layout headerTitle="Goal Templates" showBackButton>
              <TemplateSelectionPage />
            </Layout>
          
        }
      />
      <Route
        path="/goals/templates/manage"
        element={
          
            <Layout headerTitle="Manage Templates" showBackButton>
              <TemplatesManagementPage />
            </Layout>
          
        }
      />
      <Route
        path="/achievements"
        element={
          
            <Layout headerTitle="Achievements">
              <AchievementsPage />
            </Layout>
          
        }
      />
      <Route
        path="/achievements/:id"
        element={
          
            <Layout headerTitle="Achievement Details" showBackButton>
              <AchievementDetailPage />
            </Layout>
          
        }
      />

      {/* Settings routes */}
      <Route
        path="/settings"
        element={
          
            <Layout headerTitle="Settings" showBackButton>
              <SettingsPage />
            </Layout>
          
        }
      />
      <Route
        path="/settings/pending-changes"
        element={
          
            <Layout headerTitle="Pending Changes" showBackButton>
              <PendingChangesPage />
            </Layout>
          
        }
      />
      <Route
        path="/settings/connected-services"
        element={

            <Layout headerTitle="Connected Services" showBackButton>
              <ConnectedServicesPage />
            </Layout>

        }
      />
      <Route
        path="/settings/import-workouts"
        element={
            <Layout headerTitle="Import from Health" showBackButton>
              <ImportWorkoutsPage />
            </Layout>
        }
      />
      <Route
        path="/settings/custom-belt"
        element={
          <CustomBeltConfigurator />
        }
      />

      {/* Notifications */}
      <Route
        path="/notifications"
        element={
          
            <Layout headerTitle="Notifications" showBackButton>
              <NotificationsPage />
            </Layout>
          
        }
      />
      
      {/* More tab */}
      <Route
        path="/more"
        element={

            <Layout headerTitle="More">
              <MorePage />
            </Layout>

        }
      />

      {/* Help/Legal routes (protected - require auth) */}
      <Route
        path="/about"
        element={

            <Layout headerTitle="About" showBackButton>
              <AboutPage />
            </Layout>

        }
      />
      {/* Note: /terms and /privacy are now public routes (see above) */}
      <Route
        path="/help"
        element={

            <Layout headerTitle="Help & Support" showBackButton>
              <HelpPage />
            </Layout>

        }
      />
      <Route
        path="/feedback"
        element={

            <Layout headerTitle="Feedback" showBackButton>
              <FeedbackPage />
            </Layout>

        }
      />

      {/* What's New page */}
      <Route
        path="/whats-new"
        element={
          
            <Layout headerTitle="What's New" showBackButton>
              <WhatsNewPage />
            </Layout>
          
        }
      />
      
      {/* Coach Menu */}
      <Route
        path="/coach"
        element={

            <Layout headerTitle="Coach">
              <CoachMenuPage />
            </Layout>

        }
      />

      {/* AI Coach routes */}
      <Route
        path="/ai/chat"
        element={

            <Layout headerTitle="AI Coach" showBackButton>
              <ChatPage />
            </Layout>

        }
      />
      <Route
        path="/ai/analysis"
        element={

            <Layout headerTitle="Training Analysis" showBackButton>
              <AnalysisPage />
            </Layout>

        }
      />
      <Route
        path="/ai/recommendations"
        element={

            <Layout headerTitle="Technique Recommendations" showBackButton>
              <RecommendationsPage />
            </Layout>

        }
      />
      <Route
        path="/ai/diary"
        element={
            <AIDiaryPage />
        }
      />

      {/* Premium routes */}
      <Route
        path="/premium"
        element={
          
            <Layout headerTitle="Marune Peak" showBackButton={false}>
              <PremiumLandingPage />
            </Layout>
          
        }
      />
      <Route
        path="/premium/insights"
        element={

            <Layout headerTitle="Premium Insights" showBackButton>
              <PremiumInsightsPage />
            </Layout>

        }
      />
      <Route
        path="/premium/welcome"
        element={
          <WelcomeToPeakPage />
        }
      />
      <Route
        path="/peak/insights"
        element={

            <Layout headerTitle="Peak Insights" showBackButton>
              <PeakInsightsPage />
            </Layout>

        }
      />
      <Route
        path="/leaderboard"
        element={

            <Layout headerTitle="Leaderboard" showBackButton>
              <LeaderboardPage />
            </Layout>

        }
      />
      <Route
        path="/premium/restore"
        element={
          
            <Layout headerTitle="Restore Purchases" showBackButton>
              <RestorePurchasesPage />
            </Layout>
          
        }
      />
      {/* Debug route removed for production
      <Route
        path="/premium/debug"
        element={

            <Layout headerTitle="Premium Debug" showBackButton>
              <PremiumDebugPage />
            </Layout>

        }
      />
      */}
      <Route
        path="/premium/promo-codes"
        element={
          <PromoCodesPage />
        }
      />

      {/* Diagnostics routes - DISABLED FOR SECURITY
      <Route
        path="/diagnostics"
        element={

            <Layout headerTitle="Database Diagnostics" showBackButton backButtonAction={() => window.history.back()}>
              <DiagnosticsPage />
            </Layout>

        }
      />
      <Route
        path="/diagnostics/performance"
        element={

            <Layout headerTitle="Performance Diagnostics" showBackButton backButtonAction={() => window.history.back()}>
              <PerformancePage />
            </Layout>

        }
      />
      <Route
        path="/diagnostics/comments"
        element={

            <Layout headerTitle="Comments Diagnostics" showBackButton backButtonAction={() => window.history.back()}>
              <CommentsDiagnosticPage />
            </Layout>

        }
      />

      <Route
        path="/diagnostics/firebase-config"
        element={

            <Layout headerTitle="Firebase Config" showBackButton backButtonAction={() => window.history.back()}>
              <FirebaseConfigPage />
            </Layout>

        }
      />

      <Route
        path="/diagnostics/language"
        element={

            <Layout headerTitle="Language Diagnostics" showBackButton backButtonAction={() => window.history.back()}>
              <LanguageDiagnosticPage />
            </Layout>

        }
      />

      <Route
        path="/diagnostics/training-locations"
        element={

            <Layout headerTitle="Training Location Diagnostics" showBackButton backButtonAction={() => window.history.back()}>
              <TrainingLocationDiagnosticPage />
            </Layout>

        }
      />

      <Route
        path="/diagnostics/tagged-friends"
        element={

            <Layout headerTitle="Tagged Friends Diagnostics" showBackButton backButtonAction={() => window.history.back()}>
              <TaggedFriendsDiagnosticPage />
            </Layout>

        }
      />

      <Route
        path="/diagnostics/login-test"
        element={
          <Layout headerTitle="Login Test" showBackButton backButtonAction={() => window.history.back()}>
            <LoginTestPage />
          </Layout>
        }
      />
      */}

      {/* Post detail pages */}
      <Route
        path="/post/:id"
        element={

            <Layout headerTitle="Post" showBackButton>
              <PostDetailPage />
            </Layout>

        }
      />

      {/* Standalone Comments page */}
      <Route
        path="/comments/:postType/:postId"
        element={
          
            <Layout headerTitle="Comments" showBackButton>
              <CommentsPage />
            </Layout>
          
        }
      />
      
      {/* Weight tracking routes */}
      <Route
        path="/weight"
        element={
          
            <Layout headerTitle="Weight Tracker">
              <WeightTrackerPage />
            </Layout>
          
        }
      />
      <Route
        path="/weight/add"
        element={
          
            <Layout headerTitle="Add Weigh-In" showBackButton>
              <AddWeightEntryPage />
            </Layout>
          
        }
      />
      <Route
        path="/weight/edit/:entryId"
        element={
          
            <Layout headerTitle="Edit Weigh-In" showBackButton>
              <EditWeightEntryPage />
            </Layout>
          
        }
      />
      <Route
        path="/weight/goal"
        element={
          
            <Layout headerTitle="Weight Goal" showBackButton>
              <EditWeightGoalPage />
            </Layout>
          
        }
      />
      <Route
        path="/weight/history"
        element={
          
            <Layout headerTitle="Weight History" showBackButton>
              <WeightHistoryPage />
            </Layout>
          
        }
      />
      
      {/* Gi Tracker routes */}
      <Route
        path="/gi"
        element={
          
            <Layout headerTitle="Gi Keeper">
              <GiKeeperPage />
            </Layout>
          
        }
      />
      <Route
        path="/gi/add"
        element={
          
            <Layout headerTitle="Add Gi" showBackButton>
              <AddEditGiPage />
            </Layout>
          
        }
      />
      <Route
        path="/gi/edit/:giId"
        element={

            <Layout headerTitle="Edit Gi" showBackButton>
              <AddEditGiPage />
            </Layout>

        }
      />

      {/* Schools Directory routes */}
      <Route
        path="/schools"
        element={
          <Layout headerTitle="Schools Directory">
            <SchoolsDirectoryPage />
          </Layout>
        }
      />
      <Route
        path="/schools/add"
        element={
          <Layout headerTitle="Add School" showBackButton>
            <AddSchoolPage />
          </Layout>
        }
      />
      <Route
        path="/schools/:schoolId/claim"
        element={
          <Layout headerTitle="Claim School" showBackButton>
            <ClaimSchoolPage />
          </Layout>
        }
      />
      <Route
        path="/schools/:schoolId/edit"
        element={
          <Layout headerTitle="Edit School" showBackButton>
            <EditSchoolPage />
          </Layout>
        }
      />
      <Route
        path="/schools/:slugOrId/report"
        element={<IncidentReportPage />}
      />
      <Route
        path="/schools/:slugOrId"
        element={
          <Layout headerTitle="School Details" showBackButton>
            <SchoolDetailPage />
          </Layout>
        }
      />

      {/* Clear Mat Standard routes - Public BJJ academy directory */}
      <Route
        path="/clearmat"
        element={<ClearMatDirectoryPage />}
      />
      <Route
        path="/clearmat/school/add"
        element={<ClearMatAddSchoolPage />}
      />
      <Route
        path="/clearmat/school/:schoolId/edit"
        element={<EditSchoolPage />}
      />
      <Route
        path="/clearmat/school/:schoolId/claim"
        element={<ClaimSchoolPage />}
      />
      <Route
        path="/clearmat/school/:slugOrId/report"
        element={<IncidentReportPage />}
      />
      <Route
        path="/clearmat/incident/:incidentId"
        element={<IncidentDetailPage />}
      />
      <Route
        path="/clearmat/school/:slugOrId"
        element={<ClearMatSchoolDetailPage />}
      />

      {/* Clear Mat Events */}
      <Route
        path="/clearmat/events"
        element={<EventsPage />}
      />
      <Route
        path="/clearmat/events/submit"
        element={<SubmitEventPage />}
      />
      <Route
        path="/clearmat/events/:eventId"
        element={<EventDetailPage />}
      />

      {/* Clear Mat Blog */}
      <Route path="/clearmat/blog" element={<BlogPage />} />
      <Route path="/clearmat/blog/new" element={<BlogEditorPage />} />
      <Route path="/clearmat/blog/my-articles" element={<MyArticlesPage />} />
      <Route path="/clearmat/blog/edit/:articleId" element={<BlogEditorPage />} />
      <Route path="/clearmat/blog/:slug" element={<BlogArticlePage />} />

      {/* Clear Mat Admin routes - Moderation dashboard */}
      <Route
        path="/clearmat/admin"
        element={
          <Layout headerTitle="Clear Mat Moderation" showBackButton>
            <ClearMatAdminDashboard />
          </Layout>
        }
      />
      <Route
        path="/clearmat/admin/incidents"
        element={
          <Layout headerTitle="Clear Mat Moderation" showBackButton>
            <IncidentModerationPage />
          </Layout>
        }
      />
      <Route
        path="/clearmat/admin/schools"
        element={
          <Layout headerTitle="Clear Mat Moderation" showBackButton>
            <ClearMatSchoolManagementPage />
          </Layout>
        }
      />
      <Route
        path="/clearmat/admin/audit-log"
        element={
          <Layout headerTitle="Clear Mat Moderation" showBackButton>
            <AuditLogPage />
          </Layout>
        }
      />
      <Route
        path="/clearmat/admin/moderators"
        element={
          <Layout headerTitle="Clear Mat Moderation" showBackButton>
            <ModeratorManagementPage />
          </Layout>
        }
      />
      <Route
        path="/clearmat/admin/resources"
        element={
          <Layout headerTitle="Clear Mat Moderation" showBackButton>
            <ResourceManagementPage />
          </Layout>
        }
      />
      <Route
        path="/clearmat/admin/events"
        element={
          <Layout headerTitle="Clear Mat Moderation" showBackButton>
            <EventModerationPage />
          </Layout>
        }
      />
      <Route
        path="/clearmat/admin/reviews"
        element={
          <Layout headerTitle="Clear Mat Moderation" showBackButton>
            <ReviewModerationPage />
          </Layout>
        }
      />
      <Route
        path="/clearmat/admin/responses"
        element={
          <Layout headerTitle="Clear Mat Moderation" showBackButton>
            <ResponseModerationPage />
          </Layout>
        }
      />
      <Route
        path="/clearmat/admin/contributors"
        element={
          <Layout headerTitle="Clear Mat Moderation" showBackButton>
            <ContributorManagementPage />
          </Layout>
        }
      />
      <Route
        path="/clearmat/admin/editors"
        element={
          <Layout headerTitle="Clear Mat Moderation" showBackButton>
            <EditorManagementPage />
          </Layout>
        }
      />
      <Route
        path="/clearmat/admin/blog"
        element={
          <Layout headerTitle="Clear Mat Moderation" showBackButton>
            <BlogModerationPage />
          </Layout>
        }
      />

      {/* Admin routes */}
      <Route
        path="/admin"
        element={
          
            <Layout headerTitle="Admin Dashboard">
              <AdminDashboard />
            </Layout>
          
        }
      />
      <Route
        path="/admin/users"
        element={
          
            <Layout headerTitle="User Management" showBackButton>
              <UserManagementPage />
            </Layout>
          
        }
      />
      <Route
        path="/admin/database"
        element={
          
            <Layout headerTitle="Database Management" showBackButton>
              <DatabaseManagementPage />
            </Layout>
          
        }
      />
      <Route
        path="/admin/activity"
        element={
          
            <Layout headerTitle="Activity Monitoring" showBackButton>
              <ActivityMonitoringPage />
            </Layout>
          
        }
      />
      <Route
        path="/admin/peak-subscriptions"
        element={
          
            <Layout headerTitle="Peak Subscriptions" showBackButton>
              <PeakSubscriptionPage />
            </Layout>
          
        }
      />
      <Route
        path="/admin/import-bjj-locations"
        element={

            <Layout headerTitle="Import BJJ Locations" showBackButton>
              <BJJLocationsImport />
            </Layout>

        }
      />
      <Route
        path="/admin/schools"
        element={

            <Layout headerTitle="School Management" showBackButton>
              <SchoolManagementPage />
            </Layout>

        }
      />

      {/* Test/debug page routes removed for production
      <Route
        path="/test-firebase-trainings"
        element={

            <Layout headerTitle="Firebase Training Test" showBackButton>
              <FirebaseTestTrainings />
            </Layout>

        }
      />

      <Route
        path="/test-fresh-install"
        element={

            <Layout headerTitle="Fresh Install Test" showBackButton>
              <FreshInstallTestPage />
            </Layout>

        }
      />

      <Route
        path="/gymtimer"
        element={

            <Layout headerTitle="Gym Timer" showBackButton>
              <GymTimerPage />
            </Layout>

        }
      />
      */}

{/* iOS Auth Testing route removed for production simplification */}
      
      {/* Temporary route for creating sample data - keep for now, may be useful for new users
      <Route
        path="/create-sample-routines"
        element={

            <Layout headerTitle="Create Sample Data" showBackButton>
              <CreateSampleRoutines />
            </Layout>

        }
      />
      */}
      
      
      
      {/* Home Dashboard route */}
      <Route
        path="/home"
        element={
          
            <Layout headerTitle="Home">
              <CriticalLazyRoutes.HomePage />
            </Layout>
          
        }
      />
      
      {/* Default route - redirect to home if authenticated, otherwise to login */}
      <Route 
        path="/" 
        element={<Navigate to="/home" replace />} 
      />
      
      {/* Catch-all for 404 */}
      <Route
        path="*"
        element={
          <Layout>
            <NotFoundPage />
          </Layout>
        }
      />
    </Routes>
    </AppNavigationWrapper>
  );
};

export default AppRoutes;